import { useMemo, useState } from 'react';
import { Box, Button, Group, Heading, SearchInput, Text } from '@optiaxiom/react';
import { IconPlus } from '@optiaxiom/icons';
import { useApps, type RegisteredApp } from '@/lib/api';
import { AppRegisteredModal } from './AppRegisteredModal';
import { AppsEmptyState } from './AppsEmptyState';
import { AppsError } from './AppsError';
import { AppsLoading } from './AppsLoading';
import { AppsTable } from './AppsTable';
import { RegisterAppModal } from './RegisterAppModal';
import styles from './AppsPage.module.css';

export function AppsPage() {
  const { data, isLoading, isError, isFetching, refetch } = useApps();
  const [query, setQuery] = useState('');
  const [registerOpen, setRegisterOpen] = useState(false);
  // Set after a successful registration to show the "what next" (scaffold) modal.
  const [registeredApp, setRegisteredApp] = useState<RegisteredApp | null>(null);

  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const degradedShards = data?.degradedShards ?? [];

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (app) => app.name.toLowerCase().includes(q) || app.appId.toLowerCase().includes(q),
    );
  }, [items, query]);

  const hasApps = !isLoading && !isError && items.length > 0;

  return (
    <div className={styles.page}>
      <Group flexDirection="row" justifyContent="space-between" alignItems="end" gap="16">
        <Group flexDirection="column" gap="4" alignItems="start">
          <Heading level="1">Apps</Heading>
        </Group>
        {hasApps && (
          <Button
            appearance="primary"
            size="lg"
            fontSize="md"
            addonBefore={<IconPlus />}
            onClick={() => setRegisterOpen(true)}
          >
            Register app
          </Button>
        )}
      </Group>

      {isLoading && <AppsLoading />}
      {!isLoading && isError && <AppsError onRetry={() => void refetch()} retrying={isFetching} />}
      {!isLoading && !isError && items.length === 0 && (
        <AppsEmptyState onRegister={() => setRegisterOpen(true)} />
      )}
      {/* One card: the search row sits on top (divided by a bottom border) and the table fills
          the rest. DataTable draws no outer border of its own, so this single frame wraps both
          without any doubled edges, and overflow:hidden clips it all to the rounded corners. */}
      {hasApps && (
        <Box
          mt="24"
          border="1"
          borderColor="border.secondary"
          rounded="lg"
          overflow="hidden"
          className={styles.listPanel}
        >
          <Box borderB="1" borderColor="border.secondary" p="16" className={styles.searchRow}>
            <SearchInput
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onValueClear={() => setQuery('')}
              placeholder="Filter apps by name or App ID"
              className={styles.search}
            />
          </Box>
          {filtered.length > 0 ? (
            <Box overflow="hidden" className={styles.tableBox}>
              <AppsTable apps={filtered} degradedShards={degradedShards} />
            </Box>
          ) : (
            <Box p="24">
              <Text color="fg.secondary" textAlign="center">
                No apps match &ldquo;{query}&rdquo;.
              </Text>
            </Box>
          )}
        </Box>
      )}

      <RegisterAppModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onRegistered={(app) => {
          setRegisterOpen(false);
          setRegisteredApp(app);
        }}
      />
      <AppRegisteredModal
        open={registeredApp !== null}
        app={registeredApp}
        onClose={() => setRegisteredApp(null)}
      />
    </div>
  );
}
